/// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';

    export interface IConnectionType {
        name: string;
    }

    export class ConnectionType {
        public static SRT: string = "SRT";
        public static TSOIP: string = "TSoIP";
    }

    export interface IBlockTypeConnectionType {
        direction: string;
        action: string;
        numberOfConnections: number;
        connectionTypeName: string;
        blockType: IBlockType;
        connectionType: IConnectionType;
    }

    export interface IApplicationType {
        blocksGenerated: {
            numberOfBlocks: number,
            blockType: IBlockType,
        };
    }

    export interface IApplication {
        name: string;
        networkNodeHref: string;
        networkNodeTypeHref: string;
        applicationType: IApplicationType;
    }

    export interface IRedundantBlocks {
        primaryEncoderBlock: IBlock;
        secondaryEncoderBlock: IBlock;
        primaryDecoderBlock: IBlock;
        secondaryDecoderBlock: IBlock;
    }

    export interface IBlock {
        id: number;
        href: string;
        name: string;
        application: IApplication;
        blockType: IBlockType;
        blockTypeNaturalKey?: string;
        blockConnections: IBlockConnections[];
        isSource: () => boolean;
        locked: boolean;
        lockedComment: string;
        lockedUser: string;
        lockedTimestamp: Date;
        networkNodeHref: string;
        networkNodeTypeHref: string;
        config: IBlockConfig;
        connectionStatus?: BlockConnectionStatus;
        lockedName: boolean;
        suffix?: string;
        streamPipe?: StreamPipe;
        connRooms?: ConnectionRoom[];
        selected?: boolean;
        workflowMode?: string;
    }

    export interface IBlockWithStreamPipeConnections extends IBlock {
        streamPipeBlockConnections: StreamPipeBlockConnectionInfo[];
    }

    export interface IBundle {
        sources: IBlock[][];
        sinks: IBlock[][];
    }

    export interface IBlockConfig {
        serial?: string;
        index?: number;
        fileName?: string;
        blockName?: string;
        clientHref?: number;
        serverHref?: number;
        ts?: IBlockConfigTS;
        srt_ts?: IBlockConfigTS;
        port?: number;
        inputTargetPort?: string;
        outputTargetPort?: string;
        customCallSrcPort?: string;
        tsbpddelay?: number;
        sonHostname?: string;
        equals(blockConfig: IBlockConfig): boolean;
        isRedundant?: boolean;
        redundancyGroup?: IRedundancyGroup;
        redundancyConnection?: number;
        srtConfig: string;
        monitoring?: number;
        monitoringName?: string;
        monitoringInfo?: MonitoringInfo;
        monitoringSinkId?: number;
        forwardBlockId?: number;
        ip? : string;
        latency?: number;
        redundantBlockId?: number;
    }

    export interface MonitoringInfo {
        deviceSerialNumber: string;
        index: number;
    }

    export interface IBlockNameParameters {
        nodeName: string;
        numberOfBlocks: number;
        cpus?: number;
        cpusOffset?: number;
    }

    export class Block implements IBlock {
        public id: number;
        public name: string;
        public href: string;
        public creationDate: Date;
        public application: IApplication;
        public blockType: IBlockType;
        public blockConnections: IBlockConnections[];
        public blockTypeNaturalKey?: string;
        public locked: boolean;
        public lockedComment: string;
        public lockedUser: string;
        public lockedTimestamp: Date;
        public networkNodeHref: string;
        public networkNodeTypeHref: string;
        public config: IBlockConfig;
        public connectionStatus?: BlockConnectionStatus;
        public lockedName: boolean;

        public static Empty(): Block {
            return new Block();
        }

        /**
         * Checks if 2 blocks objects are equals. Two block objects are considered equal if they are referencing
         * to the same block, that is, they  have the same id. If any of the objects is undefined returns false
         * since it is not referencing to any block
         * @param block1 block to compare
         * @param block2 block to compare
         * @ return true if the objects are referencing to the same block, false otherwise
         */
        public static equals(block1: IBlock, block2: IBlock): boolean {
            if (block1 == undefined) {
                return false;
            }
            if (block2 == undefined) {
                return false;
            }

            return block1.id === block2.id;
        }

        constructor() {
            // empty constructor
            this.name = '';
        }

        public isSource(): boolean {
            if (this.blockType !== undefined)
                return this.blockType.pipePosition === 'SOURCE';
            else
                return false;
        }
    }

    export class BlockGroup {
        public name: string;
        public blocks: Block[] = [];
        public direction: string;
        public push: boolean;
        public connectionType: string;

        get detail(): string {
            return this.name + " (" + this.blocks.length + " blocks: " + this.blocks[0].name + ",....)";
        }

        get connection(): string {
            return this.blocks[0].href;
        }

        get numberOfBlocks(): number {
            return this.blocks.length;
        }
    }

    export class RedundantBlocks implements IRedundantBlocks {
        public primaryEncoderBlock: IBlock;
        public secondaryEncoderBlock: IBlock;
        public primaryDecoderBlock: IBlock;
        public secondaryDecoderBlock: IBlock;

        public static Empty(): RedundantBlocks {
            return new RedundantBlocks();
        }

    }

    export class BlockWithStreamPipesBlockConnections extends Block implements IBlockWithStreamPipeConnections {
        public streamPipeBlockConnections: StreamPipeBlockConnectionInfo[];

        constructor(block: IBlockWithStreamPipeConnections) {
            super();
            this.id = block.id;
            this.blockType = block.blockType;
            this.streamPipeBlockConnections = block.streamPipeBlockConnections;
        }
    }

    export class BlockConfig implements IBlockConfig {
        public serial?: string;
        public index?: number;
        public fileName?: string;
        public blockName?: string;
        public clientHref?: number;
        public serverHref?: number;
        public ts?: IBlockConfigTS;
        public srt_ts?: IBlockConfigTS;
        public port?: number;
        public inputTargetPort?: string;
        public outputTargetPort?: string;
        public customCallSrcPort?: string;
        public tsbpddelay?: number;
        public sonHostname: string;
        public srtConfig: string;
        public monitoring?: number;
        public forwardBlockId?: number;
        public redundancyConnection?: number;
        public redundancyGroup?: IRedundancyGroup;
        public isRedundant?: boolean;
        public redundantBlockId?: number;


        public static Copy(blockConfig: IBlockConfig): BlockConfig {
            let newBlockConfig: BlockConfig = new BlockConfig();
            newBlockConfig.serial = blockConfig.serial;
            newBlockConfig.index = blockConfig.index;
            newBlockConfig.fileName = blockConfig.fileName;
            newBlockConfig.blockName = blockConfig.blockName;
            newBlockConfig.clientHref = blockConfig.clientHref;
            newBlockConfig.serverHref = blockConfig.serverHref;
            if (blockConfig.ts !== undefined) {
                newBlockConfig.ts = new BlockConfigTS(blockConfig.ts.ip, blockConfig.ts.port);
            }
            if (blockConfig.srt_ts !== undefined) {
                newBlockConfig.srt_ts = new BlockConfigTS(blockConfig.srt_ts.ip, blockConfig.srt_ts.port);
            }
            newBlockConfig.port = blockConfig.port;
            newBlockConfig.inputTargetPort = blockConfig.inputTargetPort;
            newBlockConfig.outputTargetPort = blockConfig.outputTargetPort;
            newBlockConfig.customCallSrcPort = blockConfig.customCallSrcPort;
            newBlockConfig.tsbpddelay = blockConfig.tsbpddelay;
            newBlockConfig.sonHostname = blockConfig.sonHostname;
            newBlockConfig.srtConfig = blockConfig.srtConfig;
            newBlockConfig.monitoring = blockConfig.monitoring;
            newBlockConfig.forwardBlockId  = blockConfig.forwardBlockId;
            newBlockConfig.redundancyConnection  = blockConfig.redundancyConnection;
            newBlockConfig.redundancyGroup  = blockConfig.redundancyGroup;
            newBlockConfig.isRedundant  = blockConfig.isRedundant;
            newBlockConfig.redundantBlockId = blockConfig.redundantBlockId;

            return newBlockConfig;
        }

        public equals(blockConfig: IBlockConfig): boolean {
            const tsConfigEquals: boolean = (this.ts && blockConfig.ts && this.ts.equals(blockConfig.ts)) ||
                                      (!this.ts && !blockConfig.ts);
            const srtTsConfigEquals: boolean = (this.srt_ts && blockConfig.srt_ts && this.srt_ts.equals(blockConfig.srt_ts)) ||
                (!this.srt_ts && !blockConfig.srt_ts);
            return this.serial === blockConfig.serial &&
                this.index === blockConfig.index &&
                this.fileName === blockConfig.fileName &&
                this.blockName === blockConfig.blockName &&
                this.clientHref === blockConfig.clientHref &&
                this.serverHref === blockConfig.serverHref &&
                this.serial === blockConfig.serial &&
                this.port == blockConfig.port &&
                tsConfigEquals && srtTsConfigEquals &&
                this.inputTargetPort === blockConfig.inputTargetPort &&
                this.outputTargetPort === blockConfig.outputTargetPort &&
                this.customCallSrcPort === blockConfig.customCallSrcPort &&
                this.tsbpddelay === blockConfig.tsbpddelay &&
                this.forwardBlockId === blockConfig.forwardBlockId &&
                this.redundancyConnection === blockConfig.redundancyConnection &&
                this.redundancyGroup === blockConfig.redundancyGroup &&
                this.isRedundant === blockConfig.isRedundant &&
                this.redundantBlockId === blockConfig.redundantBlockId &&
                JSON.stringify(this.srtConfig) === JSON.stringify(blockConfig.srtConfig);
        }
    }

    export interface IBlockConfigTS {
        ip: string;
        port: number;
        equals(blockConfigTS: IBlockConfigTS): boolean;
    }

    export class BlockConfigTS implements IBlockConfigTS {
        public ip: string;
        public port: number;

        constructor(ip: string, port: number) {
            this.ip = ip;
            this.port = port;
        }

        public equals(blockConfigTS: IBlockConfigTS): boolean {
            return (blockConfigTS &&
                    this.ip === blockConfigTS.ip &&
                    this.port === blockConfigTS.port);
        }
    }

    export type BlockConnectionStatus =
        "NONE"| "OK"| "CONNECTING"| "CONNECTED"| "DISCONNECTING"| "READY"| "OFFLINE"| "ERROR"| "CONVERTING";

}
